// Pure logic for the Plan projects list (STRY0038867): review status, dates,
// search, facets, facet counts and sorting. No React in this file.
import { fleetPanelData, hasInventory } from '../inventory/fleetPanelData';

export const REVIEW_SEGMENTS = [
  { key: 'accepted', label: 'Accepted' },
  { key: 'inReview', label: 'In review' },
  { key: 'uncertain', label: 'Uncertain' },
  { key: 'declined', label: 'Declined' },
  { key: 'notReviewed', label: 'Not reviewed' }
];

// bz-pill tone per stored version state; 'neutral' maps to pl-pill--neutral.
export const STATE_TONE = {
  Accepted: 'ok',
  'In review': 'warn',
  Uncertain: 'info',
  Declined: 'bad',
  'Not reviewed': 'neutral'
};

const EMPTY_COUNTS = { accepted: 0, inReview: 0, uncertain: 0, declined: 0, notReviewed: 0, total: 0 };

export const reviewCounts = project => ({ ...EMPTY_COUNTS, ...(project.reviewCounts || {}) });

// Reviewed = any state other than Not reviewed — the project page's rule.
export const reviewedCount = project => {
  const c = reviewCounts(project);
  return c.total - c.notReviewed;
};

// The denominator: the project's Available Versions count (`available`, from
// useAvailableVersions) once known, else the versions opened so far.
export const versionTotal = (project, available) =>
  Math.max(available == null ? reviewCounts(project).total : available, reviewedCount(project));

export const toReviewCount = (project, available) => versionTotal(project, available) - reviewedCount(project);

export function reviewStatus(project, available) {
  if (versionTotal(project, available) === 0) return 'empty';
  return toReviewCount(project, available) > 0 ? 'needs' : 'done';
}

export const fleetSize = project => (project.inventorySnapshot && project.inventorySnapshot.totalCis) || 0;

export const hasDrift = project => {
  const data = fleetPanelData(project);
  return Boolean(data && data.hasDrift);
};

// The API returns naive UTC datetimes ("2026-10-07 13:05:00") or ISO with Z.
export function parseServerDate(value) {
  if (!value) return null;
  let text = String(value)
    .trim()
    .replace(' ', 'T')
    .replace(/(\.\d{3})\d+/, '$1');
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(text)) text += 'Z';
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value) {
  const date = parseServerDate(value);
  return date ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

export function formatDateTime(value) {
  const date = parseServerDate(value);
  return date ? date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
}

export function relativeTime(value, now = Date.now()) {
  const date = parseServerDate(value);
  if (!date) return '—';
  const minutes = Math.round((now - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return formatDate(value);
}

export const vendorLabelOf = (vendorId, vendors) => {
  const match = (vendors || []).find(v => v.value === vendorId);
  return (match && match.label) || vendorId || 'Unknown vendor';
};

export const ownerLabel = (userId, currentUserEmail) => {
  if (!userId) return 'Unknown';
  if (currentUserEmail && userId.toLowerCase() === currentUserEmail.toLowerCase()) return 'You';
  return userId;
};

export function initialsFor(userId) {
  const local = String(userId || '?').split('@')[0];
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

export function matchesQuery(project, query, ctx) {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return true;
  const fields = [
    project.name,
    project.productName,
    project.versionId,
    project.acceptedVersionId,
    ctx.versionLabel(project, project.versionId),
    ctx.versionLabel(project, project.acceptedVersionId),
    ctx.vendorLabel(project.vendorId)
  ];
  return fields.some(
    field =>
      field &&
      String(field)
        .toLowerCase()
        .includes(needle)
  );
}

export const FACETS = [
  { key: 'vendor', title: 'Vendor', value: project => project.vendorId },
  { key: 'owner', title: 'Created by', value: project => project.userId || '' },
  {
    key: 'status',
    title: 'Review status',
    value: (project, ctx) => reviewStatus(project, ctx.availableVersions(project))
  },
  { key: 'source', title: 'Source', value: project => (hasInventory(project) ? 'inventory' : 'manual') },
  { key: 'fleet', title: 'Fleet', value: project => (hasDrift(project) ? 'drift' : 'steady') }
];

const STATUS_OPTIONS = [
  { id: 'needs', label: 'Needs review' },
  { id: 'done', label: 'All versions reviewed' },
  { id: 'empty', label: 'No newer versions' }
];
const SOURCE_OPTIONS = [
  { id: 'inventory', label: 'From inventory' },
  { id: 'manual', label: 'Manual' }
];
const FLEET_OPTIONS = [{ id: 'drift', label: 'Drift since snapshot' }];

export function facetOptions(facetKey, projects, ctx) {
  if (facetKey === 'vendor') {
    return [...new Set(projects.map(p => p.vendorId))]
      .map(id => ({ id, label: ctx.vendorLabel(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }
  if (facetKey === 'owner') {
    return [...new Set(projects.map(p => p.userId || ''))]
      .map(id => ({ id, label: ownerLabel(id, ctx.currentUserEmail) }))
      .sort((a, b) => {
        if (a.label === 'You') return -1;
        if (b.label === 'You') return 1;
        return a.label.localeCompare(b.label);
      });
  }
  if (facetKey === 'status') return STATUS_OPTIONS;
  if (facetKey === 'source') return SOURCE_OPTIONS;
  if (facetKey === 'fleet') return FLEET_OPTIONS;
  return [];
}

export const emptySelection = () => Object.fromEntries(FACETS.map(f => [f.key, []]));

// OR within a facet, AND across facets; `exceptKey` ignores one facet (for its own counts).
function passesFacets(project, selection, ctx, exceptKey) {
  return FACETS.every(
    f => f.key === exceptKey || selection[f.key].length === 0 || selection[f.key].includes(f.value(project, ctx))
  );
}

export const filterProjects = (projects, query, selection, ctx) =>
  projects.filter(p => matchesQuery(p, query, ctx) && passesFacets(p, selection, ctx));

// How many projects would match if `optionId` were the only selection in its facet.
export function facetCount(projects, query, selection, ctx, facetKey, optionId) {
  const facet = FACETS.find(f => f.key === facetKey);
  return projects.filter(
    p => matchesQuery(p, query, ctx) && passesFacets(p, selection, ctx, facetKey) && facet.value(p, ctx) === optionId
  ).length;
}

export const SORTS = [
  { key: 'activity', label: 'Last activity', dir: 'desc' },
  { key: 'created', label: 'Date created', dir: 'desc' },
  { key: 'name', label: 'Name', dir: 'asc' },
  { key: 'vendor', label: 'Vendor', dir: 'asc' },
  { key: 'review', label: 'Versions to review', dir: 'desc' },
  { key: 'fleet', label: 'Fleet size', dir: 'desc' },
  { key: 'owner', label: 'Created by', dir: 'asc' }
];

export const DEFAULT_SORT = { key: 'activity', dir: 'desc' };

export const isValidSort = sort =>
  Boolean(sort && SORTS.some(s => s.key === sort.key) && (sort.dir === 'asc' || sort.dir === 'desc'));

export const defaultDirFor = key => (SORTS.find(s => s.key === key) || DEFAULT_SORT).dir;

const timeOf = value => {
  const date = parseServerDate(value);
  return date ? date.getTime() : 0;
};

function sortValue(project, key, ctx) {
  switch (key) {
    case 'activity':
      return timeOf(project.lastActivityAt || project.updatedAt);
    case 'created':
      return timeOf(project.createdAt);
    case 'name':
      return (project.name || '').toLowerCase();
    case 'vendor':
      return `${ctx.vendorLabel(project.vendorId)} ${project.name || ''}`.toLowerCase();
    case 'review':
      return toReviewCount(project, ctx.availableVersions(project));
    case 'fleet':
      return fleetSize(project);
    case 'owner': {
      const label = ownerLabel(project.userId, ctx.currentUserEmail);
      return label === 'You' ? '' : label.toLowerCase();
    }
    default:
      return 0;
  }
}

export function sortProjects(projects, sort, ctx) {
  const direction = sort.dir === 'asc' ? 1 : -1;
  return [...projects].sort((a, b) => {
    const x = sortValue(a, sort.key, ctx);
    const y = sortValue(b, sort.key, ctx);
    if (x < y) return -direction;
    if (x > y) return direction;
    return (a.name || '').localeCompare(b.name || '');
  });
}
